import { Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, Grid, IconButton, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography, Paper } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { postData } from "../../services/base.service";
import AutocompleteComponent from "../Shared/AutocompleteComponent";
import CVTable from "./CVTable";
import { deleteDocumentFromSharePoint, uploadDocumentToSharePoint } from "../../services/sharepoint.service";
import ConsentUpload from "../Shared/ConsentUpload";
import { Close, Edit, Save, Lock, Person, Stars, Add, Delete, TextSnippet, Check, History, ArrowForward, AutoAwesome } from "@mui/icons-material";
import { useMsal, useAccount } from '@azure/msal-react';
import { CVDataType, CVFileBlobType, ExpertProjectHistoryType, requiredFieldsExpertsForm } from "../../types/dataTypes";
import { GenderOptions, WorkExperienceOptions } from "../../utils/consts";
import { buildCvPayload } from "./cvPayload";
import ImageUpload from "../Shared/ImageUpload";
import { bestMatchOption, fileToBase64, getRegionsFromCountries } from "../../utils/utilFunctions";
import { useNotification } from "../Shared/NotificationProvider";


type ExpertFormProps = {
  expertData?: any,
  type: string,
  setLoading: Function,
  accessToken: string,
  setDetailsDrawerOpen: Function,
  setFilters: Function,
  onSaved?: () => void,
}

function ExpertForm({ expertData = {}, type, setLoading, accessToken, setFilters, onSaved, setDetailsDrawerOpen }: ExpertFormProps) {
  const [formData, setFormData] = useState(expertData);
  const [cvData, setCVData] = useState<CVDataType[]>(expertData.cvDetails || []);
  const [cvFileBlob, setCvFileBlob] = useState<CVFileBlobType[]>([]);
  const [deletedCvIDs, setDeletedCvIDs] = useState<number[]>([]);
  const [consentFileName, setConsentFileName] = useState(expertData.expertsDetails.consentFile || '');
  const [tabValue, setTabValue] = useState(0);
  const [isEditMode, setIsEditMode] = useState(type === 'add');
  const [saveFormData, setSaveFormData] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [paragraphLoading, setParagraphLoading] = useState(false);
  const [paragraphError, setParagraphError] = useState<string | null>(null);
  const [autoFillLoading, setAutoFillLoading] = useState(false);
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const navigate = useNavigate();
  const notify = useNotification();
  const projectHistory: ExpertProjectHistoryType[] = formData.projectHistory || [];

  const handleTabChange = (event: any, newValue: any) => {
    setTabValue(newValue);
  };

  const toggleEditMode = () => {
    ;
    setIsEditMode(!isEditMode);
    //notify the user that the form is in edit mode
    notify(isEditMode ? 'Form is now in view mode. No changes possible now.' :
      'Form is now in edit mode. You can now edit the form and dont forget to hit save in the end.', 'info');
  };

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prevData: any) => ({ ...prevData, expertsDetails: { ...prevData.expertsDetails, [name]: value } }));
  };

  const handleCheckboxChange = (event: any) => {
    const { name, checked } = event.target;
    setFormData((prevData: any) => ({ ...prevData, expertsDetails: { ...prevData.expertsDetails, [name]: checked } }));
  };

  const handleYearsOfExperienceChange = (event: any, newValue: any) => {
    setFormData((prevData: any) => ({ ...prevData, expertsDetails: { ...prevData.expertsDetails, years_of_work_experience: newValue } }));
  };

  const handleGenderChange = (event: any, newValue: any) => {
    setFormData((prevData: any) => ({ ...prevData, expertsDetails: { ...prevData.expertsDetails, gender: newValue } }));
  };

  const handleLanguageChange = (event: any, newValue: any) => {
    setFormData((prevData: any) => ({ ...prevData, appliedOptions: { ...prevData.appliedOptions, languages: newValue } }));
  };

  const handleDonorChange = (event: any, newValue: any) => {
    setFormData((prevData: any) => ({ ...prevData, appliedOptions: { ...prevData.appliedOptions, donors: newValue } }));
  };

  const handleRegionChange = (event: any, newValue: any) => {
    setFormData((prevData: any) => ({ ...prevData, appliedOptions: { ...prevData.appliedOptions, regions: newValue } }));
  };

  const handleNationalityChange = (event: any, newValue: any) => {
    setFormData((prevData: any) => ({ ...prevData, appliedOptions: { ...prevData.appliedOptions, nationalities: newValue } }));
  };

  const handleKeywordChange = (event: any, newValue: any) => {
    setFormData((prevData: any) => ({ ...prevData, appliedOptions: { ...prevData.appliedOptions, keywords: newValue } }));
  };

  // Mirrors PartnerForm's handleLogoChange - keeps the raw File around for upload on save,
  // while showing an immediate local preview via an object URL.
  const handlePhotoChange = (file: File | null) => {
    setPhotoFile(file);
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setFormData((prevData: any) => ({ ...prevData, expertsDetails: { ...prevData.expertsDetails, photo: objectUrl } }));
    } else {
      setFormData((prevData: any) => ({ ...prevData, expertsDetails: { ...prevData.expertsDetails, photo: null } }));
    }
  };

  // Sends whatever is currently in the form (including unsaved edits) to the AI paragraph
  // endpoint - manually triggered only, never automatic on save, so it never silently
  // overwrites a manually-written paragraph and never costs an API call on unrelated edits.
  const handleGenerateParagraph = async () => {
    setParagraphLoading(true);
    setParagraphError(null);
    try {
      const expertSummary = {
        firstName: formData.expertsDetails.firstName,
        lastName: formData.expertsDetails.lastName,
        gender: formData.expertsDetails.gender,
        yearsOfWorkExperience: formData.expertsDetails.years_of_work_experience,
        nationalities: formData.appliedOptions?.nationalities || [],
        languages: formData.appliedOptions?.languages || [],
        regions: formData.appliedOptions?.regions || [],
        donors: formData.appliedOptions?.donors || [],
        keywords: formData.appliedOptions?.keywords || [],
      };
      const payload = new FormData();
      payload.append('expertSummary', JSON.stringify(expertSummary));
      // "Always use the latest CV": for an existing expert, the backend looks up the latest
      // already-persisted CV by expertID (see AiRouterService). For a brand-new expert being
      // added, nothing is persisted yet, so send the most recently attached CV file directly -
      // it hasn't been through server-side text extraction yet either.
      if (type === 'update' && formData.expertsDetails.expertID) {
        payload.append('expertID', String(formData.expertsDetails.expertID));
      } else if (cvFileBlob.length > 0) {
        payload.append('cvFile', cvFileBlob[cvFileBlob.length - 1].blob);
      }

      const response: any = await postData('ai/generateExpertParagraph', payload, 'json', undefined, false, 'none', '*/*');
      if (!response || response.success === false || !response.paragraph) {
        throw new Error(response?.message || response?.error || 'Unable to generate paragraph.');
      }
      setFormData((prevData: any) => ({ ...prevData, expertsDetails: { ...prevData.expertsDetails, paragraph: response.paragraph } }));
    } catch (error: any) {
      setParagraphError(error.message || 'Unable to generate paragraph.');
    } finally {
      setParagraphLoading(false);
    }
  };

  // Opt-in AI prefill for a brand-new expert: the file picked here is used only to read the CV's
  // content - it's separate from the "official" CV upload in the CV tab, which is what actually
  // gets attached to the expert on save. Only ever fills fields that are currently empty and
  // only auto-selects languages/regions/nationality when confidently matched against this form's
  // own dropdown options - anything unmatched is left for the user to pick manually rather than
  // guessed at.
  const handleAutoFillFromCv = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file if the user wants to retry
    if (!file) return;

    setAutoFillLoading(true);
    try {
      const payload = new FormData();
      payload.append('cvFile', file);
      const response: any = await postData('ai/extractExpertFromCv', payload, 'json', undefined, false, 'none', '*/*');
      if (!response || response.success === false || !response.extracted) {
        throw new Error(response?.message || response?.error || 'Unable to extract details from this CV.');
      }
      const extracted = response.extracted;

      const languageOptions: string[] = formData.dropdownOptions.languages.map((l: any) => l.language);
      const matchedLanguages = languageOptions.filter((option) =>
        (extracted.languages || []).some((name: string) => name.trim().toLowerCase() === option.trim().toLowerCase()));

      const regionNamesFromCv = getRegionsFromCountries(extracted.countries || []).map((r: any) => r.key);
      const regionOptions: string[] = formData.dropdownOptions.regions.map((r: any) => r.region);
      const matchedRegions = regionOptions.filter((option) =>
        regionNamesFromCv.some((name: string) => name.trim().toLowerCase() === option.trim().toLowerCase()));

      const countryOptions: string[] = formData.dropdownOptions.countries.map((c: any) => c.Country);
      const nationalityMatch = bestMatchOption(extracted.nationality, countryOptions);

      setFormData((prevData: any) => ({
        ...prevData,
        expertsDetails: {
          ...prevData.expertsDetails,
          firstName: prevData.expertsDetails.firstName || extracted.firstName || prevData.expertsDetails.firstName,
          lastName: prevData.expertsDetails.lastName || extracted.lastName || prevData.expertsDetails.lastName,
          birthdate: prevData.expertsDetails.birthdate || extracted.birthdate || prevData.expertsDetails.birthdate,
        },
        appliedOptions: {
          ...prevData.appliedOptions,
          languages: matchedLanguages.length > 0 ? matchedLanguages : prevData.appliedOptions.languages,
          regions: matchedRegions.length > 0 ? matchedRegions : prevData.appliedOptions.regions,
          nationalities: nationalityMatch ? [nationalityMatch] : prevData.appliedOptions.nationalities,
        },
      }));

      const unmatchedNote = !nationalityMatch && extracted.nationality
        ? ` Couldn't confidently match nationality "${extracted.nationality}" - please select it manually.`
        : '';
      notify(`Prefilled from CV. Please review every field before saving.${unmatchedNote}`, 'success');
    } catch (error: any) {
      notify(error.message || 'Unable to extract details from this CV.', 'error');
    } finally {
      setAutoFillLoading(false);
    }
  };

  const handleSave = async (type: string) => {
    setLoading(true);
    // check if required fields are filled
    if (!formvalidityCheck(formData.expertsDetails)) {
      notify('Please fill in all required fields First Name, Last Name and Email', 'warning');
      setSaveFormData(true);
      setLoading(false);
      return;
    }

    const updatedExpertDetails = { ...formData.expertsDetails, consentFile: consentFileName };
    if (Object.keys(formData.expertsDetails).length === 0 || formData.expertsDetails.firstName === '' || formData.expertsDetails.lastName === '') {
      notify('Please enter first name and last name', 'warning');
      setLoading(false);
      return;
    }
    else if (type === 'add' && cvData.length === 0) {
      notify('Please upload a CV', 'warning');
      setLoading(false);
      return;
    }
    else {
      const updatedFormData = {
        ...formData,
        expertsDetails: updatedExpertDetails,
      };

      if (type === 'add') {
        updatedFormData.expertsDetails.created_by = account?.username;
        updatedFormData.expertsDetails.created_at = new Date().toISOString();
        updatedFormData.expertsDetails.updated_at = null;
        updatedFormData.expertsDetails.updated_by = null;
      }
      else {
        updatedFormData.expertsDetails.updated_at = new Date().toISOString();
        updatedFormData.expertsDetails.updated_by = account?.username;
      }

      // Upload newly added CV files to SharePoint before saving, so the DB never points at a file
      // that isn't there (the CV links open straight from the SharePoint CVs library).
      const newCvFiles = cvFileBlob.filter((file) => cvData.some((cv) => cv.isNew && cv.cvFile === file.fileName));
      try {
        for (const file of newCvFiles) {
          await uploadDocumentToSharePoint(file.blob, accessToken);
        }
      } catch (error: any) {
        notify(`Uploading the CV to SharePoint failed, nothing was saved. Please try again. (${error?.message || error})`, 'error');
        setLoading(false);
        return;
      }

      const expert = new FormData();

      if (Object.keys(cvFileBlob).length > 0) {
        cvFileBlob.forEach((file) => {
          expert.append('files', file.blob);
        });
      }

      if (type === 'update') {
        //weird bug where region donor and language are listed when update #TODO check later
        delete updatedFormData.expertsDetails.region;
        delete updatedFormData.expertsDetails.donor;
        delete updatedFormData.expertsDetails.language;
      }

      const cvPayload = buildCvPayload(cvData, deletedCvIDs);
      updatedFormData.cvDetails = cvPayload.cvDetails;
      updatedFormData.deletedCvIDs = cvPayload.deletedCvIDs;

      // Photo upload - same shape as Partner's logoData: { file, fileName, mimeType, size },
      // with `file` being the base64 data URL. Only sent when a new photo was picked.
      if (photoFile) {
        updatedFormData.photoData = {
          file: await fileToBase64(photoFile),
          fileName: photoFile.name,
          mimeType: photoFile.type,
          size: photoFile.size,
        };
      }

      expert.append('data', JSON.stringify(updatedFormData));

      await postData('experts/add', expert, 'json', undefined, false, 'none', '*/*').then(async (response: any) => {
        if (!response || response.success === false || response.error) {
          throw new Error(response?.message || response?.error || 'Unable to save expert changes.');
        }
        console.log(response.message);
        if (type === 'add') {
          notify(response.message, 'success'); // Show the response text to the user
          setLoading(false);
        }
        else {
          notify('Expert updated successfully', 'success');
          setLoading(false);
        }
        setDetailsDrawerOpen(false);
        setLoading(false);
        if (onSaved) {
          // Keep the user's filters/page/sort; just re-fetch the current list page once.
          onSaved();
        } else {
          setFilters({
            names: [],
            languages: [],
            regions: [],
            donors: [],
            keywords: [],
            fulltext: [],
            nationalities: [],
          });
        }
      }).catch((error) => {
        console.error(error);
        notify(error.message || 'Unable to save expert changes.', 'error');
        setLoading(false);
      });
    }
  }


  const formvalidityCheck = (expertDetails: any): boolean => {
    let requiredFieldList = requiredFieldsExpertsForm;
    //check if all required fields are filled via a loop over the required fields and check if they are not undefined or empty
    //if they are, return false and log the error
    for (const field of requiredFieldList) {
      if (expertDetails[field as keyof any] === undefined || expertDetails[field as keyof any] === '') {
        console.error(`Validation error: ${field} is required.`);
        return false;
      }
    }
    return true;
  }

  const handleDelete = async (type: string, deleteCompletetly: boolean) => {
    if (deleteCompletetly) {
      const isConfirmed = window.confirm("Are you sure you want to delete this expert?");
      if (!isConfirmed) {
        return;
      }
    }

    setLoading(true);
    await postData('experts/delete', { expertID: formData.expertID }).then(async (response) => {
      if (response.cvFilenames) {
        for (let i = 0; i < response.cvFilenames.length; i++) {
          await deleteDocumentFromSharePoint(response.cvFilenames[i].cvlink, accessToken);
        }
      }
      if (type === 'update' && deleteCompletetly) {
        notify(response.message, 'success');
        setDetailsDrawerOpen(false);
        setLoading(false);
        setFilters({
          names: [],
          languages: [],
          regions: [],
          donors: [],
          keywords: [],
          fulltext: [],
        });
      }
    });
  };

  return (
    <div>
      <Grid container spacing={2} sx={{
        borderBottom: 1,
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        background: "white",
        zIndex: 1100,
        boxShadow: 1
      }}>

        <Grid container alignItems="center">
          <Grid item xs={6} md={6} style={{ display: 'flex', alignItems: 'center', padding: 10 }}>
            {type === 'add' ? (
              <Typography paddingLeft={3} variant="h5">Add a new Expert</Typography>
            ) : null}
          </Grid>
          <Grid item xs={6} md={6} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: 10 }}>
            {type === 'update' && (
              <Button
                variant="outlined"
                startIcon={isEditMode ? <Lock /> : <Edit />}
                onClick={toggleEditMode}
                color="primary"
                style={{ marginRight: 10 }}
              >
                {isEditMode ? 'Lock' : 'Edit'}
              </Button>
            )}
            {(type === 'update' && isEditMode) || type === 'add' ? (
              <Button
                variant="outlined"
                color="primary"
                startIcon={isEditMode ? <Save /> : <Save />}
                style={{ marginRight: 10 }}
                onClick={() => handleSave(type)}
              >
                {'Save'}
              </Button>
            ) : null}
            {type === 'add' ? (
              <IconButton onClick={() => setDetailsDrawerOpen(false)}>
                <Close />
              </IconButton>
            ) : null}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
            <Tab label={<span style={{ fontWeight: 'bold' }}>General</span>} {...a11yProps(0)} />
            <Tab label={<span style={{ fontWeight: 'bold' }}>CV</span>} {...a11yProps(1)} />
            <Tab label={<span style={{ fontWeight: 'bold' }}>Project History</span>} {...a11yProps(2)} disabled={type !== 'update'} />
            <Tab label={<span style={{ fontWeight: 'bold' }}>AI Summary</span>} {...a11yProps(3)} />
          </Tabs>
        </Grid>
      </Grid>
      <TabPanel value={tabValue} index={0}>
        {type === 'add' && (
          <Box sx={{ border: '1px dashed', borderColor: 'primary.light', borderRadius: 2, padding: 2, backgroundColor: 'action.hover', marginBottom: 3 }}>
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
              <AutoAwesome color="primary" />
              <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 240 }}>
                Have a CV ready? Let AI prefill this expert's name, birthdate, nationality,
                languages and regions from it. You'll still need to upload the CV itself in the
                CV tab for it to be saved with the expert - and you can review and correct every
                field below before saving.
              </Typography>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={autoFillLoading ? <CircularProgress size={16} /> : <AutoAwesome />}
                disabled={autoFillLoading}
              >
                Auto-fill from CV
                <input type="file" hidden accept=".pdf,.doc,.docx,.txt,.html,.rtf" onChange={handleAutoFillFromCv} />
              </Button>
            </Stack>
          </Box>
        )}
        <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }} paddingTop={2}>
          <Grid item md={12}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Stars />
              <Typography variant="h6">Experience</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteComponent
              options={formData.dropdownOptions.languages.map((language: any) => language.language)}
              value={formData.appliedOptions.languages !== undefined && formData.appliedOptions.languages !== null ?
                (formData.appliedOptions.languages[0] === null ?
                  undefined : formData.appliedOptions.languages) : undefined}
              handleChange={handleLanguageChange}
              isMulti={true}
              label="Experts Languages"
              disabled={!isEditMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteComponent
              options={formData.dropdownOptions.donors.map((donor: any) => donor.donor)}
              value={formData.appliedOptions.donors !== undefined && formData.appliedOptions.donors !== null ?
                (formData.appliedOptions.donors[0] === null ?
                  undefined : formData.appliedOptions.donors) : undefined} handleChange={handleDonorChange}
              isMulti={true}
              label="Donors"
              disabled={!isEditMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteComponent
              options={formData.dropdownOptions.regions.map((region: any) => region.region)}
              value={formData.appliedOptions.regions !== undefined && formData.appliedOptions.regions !== null ?
                (formData.appliedOptions.regions[0] === null ?
                  undefined : formData.appliedOptions.regions) : undefined}
              handleChange={handleRegionChange}
              isMulti={true}
              label="Regions"
              disabled={!isEditMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
          <AutocompleteComponent
              options={formData.dropdownOptions.keyWords.map((keyword:any) => keyword.keyword)}
              value={formData.appliedOptions.keywords || undefined}
              handleChange={handleKeywordChange}
              isMulti={true}
              label="Keywords"
              disabled={!isEditMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
          <AutocompleteComponent
              options={WorkExperienceOptions}
              value={formData.expertsDetails.years_of_work_experience || undefined}
              handleChange={handleYearsOfExperienceChange}
              isMulti={false}
              label="Years of Work Experience"
              disabled={!isEditMode}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} md={12} paddingTop={2}>
          <Divider style={{ color: 'lightgrey' }} />
        </Grid>
        <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }} paddingTop={2}>
          <Grid item md={12}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Person />
              <Typography variant="h6">Contact</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField InputLabelProps={{ shrink: true }} label="First Name" name="firstName" value={formData.expertsDetails.firstName || ''}
              onChange={handleInputChange} required InputProps={{ readOnly: !isEditMode }} error={saveFormData && formData.expertsDetails.firstName === ''} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField InputLabelProps={{ shrink: true }} style={{ width: '100%' }} label="Last Name" name="lastName"
              value={formData.expertsDetails.lastName || ''} onChange={handleInputChange} required InputProps={{ readOnly: !isEditMode }}
              error={saveFormData && formData.expertsDetails.lastName === ''} />
          </Grid>
          <Grid item xs={12} md={4}>
            <AutocompleteComponent
                options={GenderOptions}
                value={formData.expertsDetails.gender || undefined}
                handleChange={handleGenderChange}
                isMulti={false}
                label="Gender"
                disabled={!isEditMode}
              />
            </Grid>
          <Grid item xs={12} md={4}>
             <AutocompleteComponent
                options={formData.dropdownOptions.countries.map((c: any) => c.Country) !== undefined ? formData.dropdownOptions.countries.map((c: any) => c.Country) : []}
                value={formData.appliedOptions.nationalities !== undefined && formData.appliedOptions.nationalities !== null ?
                  (formData.appliedOptions.nationalities[0] === null ?
                    undefined : formData.appliedOptions.nationalities) : undefined}
                handleChange={handleNationalityChange}
                isMulti={true}
                label="Nationality"
                disabled={!isEditMode}
              />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Birthdate" value={formData.expertsDetails.birthdate || ''} onChange={handleInputChange}
              type="date" InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !isEditMode }} name='birthdate' />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Email" value={formData.expertsDetails.email || ''} onChange={handleInputChange}
              InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !isEditMode }} name='email' type="email"
              required error={saveFormData && formData.expertsDetails.email === ''} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Phone Number" value={formData.expertsDetails.phone || ''} onChange={handleInputChange}
              InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !isEditMode }} name='phone' type="phone" />
          </Grid>
            <Grid item xs={12} md={6}>
            <ImageUpload
              label="Photo"
              value={photoFile || formData.expertsDetails.photo}
              onChange={handlePhotoChange}
              disabled={!isEditMode}
              maxSize={5}
              acceptedFormats={['image/jpeg', 'image/jpg', 'image/png', 'image/gif']}
              previewSize={120}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} md={12} paddingTop={2}>
          <Divider style={{ color: 'lightgrey' }} />
        </Grid>
        <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }} paddingTop={2}>
          <Grid item md={12}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Add />
              <Typography variant="h6">Additional</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Remarks" value={formData.expertsDetails.remarks || ''} onChange={handleInputChange}
              InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !isEditMode }} name='remarks'
              multiline rows={3} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Expert Source" value={formData.expertsDetails.expert_source || ''} onChange={handleInputChange}
              multiline rows={3} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !isEditMode }} name='expert_source' fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel control={<Checkbox size='small' checked={formData.expertsDetails.favorite} indeterminate={formData.expertsDetails.favorite === undefined}
              style={{ color: saveFormData && formData.expertsDetails.favorite === undefined ? 'red' : 'inherit' }}
              onChange={handleCheckboxChange} name="favorite" />} label="Favorite" disabled={!isEditMode} />
          </Grid>
          {type === 'update' && (
            <>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Expert Created By"
                  value={formData.expertsDetails.created_by || ''}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: !isEditMode }}
                  name='created_by'
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Expert Created at"
                  value={formData.expertsDetails.created_at || ''}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: !isEditMode }}
                  name='created_at'
                  disabled
                />
              </Grid>
              </>
            )}
              <Grid item xs={12} md={4}>
              <FormControlLabel control={<Checkbox size='small' checked={formData.expertsDetails.proposalWriter} indeterminate={formData.expertsDetails.proposalWriter === undefined}
                style={{ color: saveFormData && formData.expertsDetails.proposalWriter === undefined ? 'red' : 'inherit' }}
                onChange={handleCheckboxChange} name="proposalWriter" />} label="Proposal Writer" disabled={!isEditMode} />
              </Grid>    
          {type === 'update' && isEditMode && (
            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => handleDelete(type, true)}
              >
                Delete
              </Button>
            </Grid>
          )}
        </Grid>
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3} margin={2}>
          <Grid item md={12}>
            <Stack direction="row" alignItems="center" gap={1}>
              <TextSnippet />
              <Typography variant="h6">CV Versions</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={10}>
            <CVTable headers={['cvFile', 'cvLanguage', 'cvDate', 'cvUploadedBy', 'viewCV']}
              setCVData={setCVData} accessToken={accessToken} setCvFileBlob={setCvFileBlob}
              cvData={cvData} cvFileBlob={cvFileBlob} isEditMode={isEditMode}
              deletedCvIDs={deletedCvIDs} setDeletedCvIDs={setDeletedCvIDs}/>
          </Grid>
        </Grid>
        <Grid item xs={12} md={12} paddingTop={2}>
          <Divider style={{ color: 'lightgrey' }} />
        </Grid>
        <Grid container spacing={3} margin={2} marginTop={3}>
          <Grid item md={12}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Check />
              <Typography variant="h6">Consent</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={5} justifyContent="right">
            <ConsentUpload setConsentFileName={setConsentFileName} accessToken={accessToken} type={type} isEditMode={isEditMode} />
          </Grid>
          <Grid item xs={12} sm={5} >
            <TextField
              variant="outlined"
              label={'Consent File Name'}
              name="consentFile"
              value={consentFileName || ''}
              disabled={true}
              inputProps={
                { readOnly: true }
              }
            />
          </Grid>
          <Grid item xs={12} sm={5} >
          <FormControlLabel control={<Checkbox size='small' checked={formData.expertsDetails.consent} indeterminate={formData.expertsDetails.consent === undefined}
                style={{ color: saveFormData && formData.expertsDetails.consent === undefined ? 'red' : 'inherit' }}
                onChange={handleCheckboxChange} name="consent" />} label="Consent given" disabled={!isEditMode} />
          </Grid>
        </Grid>
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3} margin={2}>
          <Grid item md={12}>
            <Stack direction="row" alignItems="center" gap={1}>
              <History />
              <Typography variant="h6">Project History</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            {projectHistory.length > 0 ? (
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <TableContainer component={Paper} sx={{ minWidth: 520 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Project #</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Position</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Days</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Rate/Day</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Ref.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {projectHistory.map((item, index) => (
                        <TableRow
                          key={`${item.projectNo}-${index}`}
                          sx={{ "&:nth-of-type(odd)": { backgroundColor: "#f2f2f2" } }}
                        >
                          <TableCell>{item.projectNo}</TableCell>
                          <TableCell sx={{ maxWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {item.projectTitle}
                          </TableCell>
                          <TableCell>
                            {item.position}
                            {item.source === 'Partner' && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                via {item.partnerName || 'partner'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{item.daysWorked ?? ''}</TableCell>
                          <TableCell align="right">{item.ratePerDay ?? ''}</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => navigate(`/references/${item.projectNo}`)}>
                              <ArrowForward fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Typography color="textSecondary" sx={{ p: 2 }}>
                This expert has not been linked to any reference yet.
              </Typography>
            )}
          </Grid>
        </Grid>
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={2} margin={2}>
          <Grid item md={12}>
            <Stack direction="row" alignItems="center" gap={1}>
              <AutoAwesome />
              <Typography variant="h6">AI Summary</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              This "golden paragraph" is a short, third-person professional summary of the expert -
              the kind of blurb used in a proposal or CV cover sheet. Generating it with AI draws on
              this expert's profile (languages, regions, donors, keywords, years of experience) and
              their most recently uploaded CV, so it reflects real project experience rather than
              just the categorical fields above. You can freely edit the result afterward, or write
              your own from scratch - nothing here is saved until you hit Save on the form.
              It's also intended to become the searchable text behind an upcoming AI-powered expert
              search, so a clear, accurate paragraph here will make that search better for everyone.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ marginBottom: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={paragraphLoading ? <CircularProgress size={16} /> : <AutoAwesome />}
                onClick={handleGenerateParagraph}
                disabled={!isEditMode || paragraphLoading}
              >
                {formData.expertsDetails.paragraph ? 'Regenerate with AI' : 'Generate with AI'}
              </Button>
              {paragraphError && (
                <Typography variant="caption" color="error">{paragraphError}</Typography>
              )}
            </Stack>
            <TextField
              value={formData.expertsDetails.paragraph || ''}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: !isEditMode }}
              name='paragraph'
              placeholder="A short professional summary - write your own, or generate one from this expert's profile and latest CV."
              fullWidth
              multiline
              minRows={16}
            />
          </Grid>
        </Grid>
      </TabPanel>
    </div>
  );
}


function TabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: any) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default ExpertForm;